// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss } from "@guestsupply/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Children, FC, useState } from "react";

export interface LinesHeaderProps {
    extendedStyles?: LinesHeaderStyles;
    hideOnTablet?: boolean;
    [key: string]: any;
    showSurcharges?: boolean;
}
interface OwnProps {
    ItemDescription: React.FC;
    PriceUnit: React.FC;
    Qty: React.FC;
    Total: React.FC;
}

export interface LinesHeaderStyles {
    wrapper?: InjectableCss;
    itemDescription?: InjectableCss;
    rightSection?: InjectableCss;
    qty?: InjectableCss;
    priceUnit?: InjectableCss;
    total?: InjectableCss;
    topSectionBorderStyles?: InjectableCss;
    surcharges?: InjectableCss;
}

const NAMES = {
    ItemDescription: "ItemDescription",
    PriceUnit: "PriceUnit",
    Qty: "Qty",
    Total: "Total",
};

const ItemDescription: FC = () => null;
const PriceUnit: FC = () => null;
const Qty: FC = () => null;
const Total: FC = () => null;

export const linesHeaderStyles: LinesHeaderStyles = {};

const LinesHeader: React.FC<LinesHeaderProps> & OwnProps = ({
    children,
    extendedStyles,
    hideOnTablet = true,
    showSurcharges,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(linesHeaderStyles, extendedStyles));
    const { ItemDescription, PriceUnit, Qty, Total, Surcharges } = React.useMemo(() => {
        const childrenArray = Children.toArray(children) as React.ReactElement[];
        const MappedChilds = childrenArray.reduce((results, current) => {
            const currentName = (current.type as React.NamedExoticComponent<any>)?.displayName;
            if (currentName) {
                results[currentName] = current;
            }
            return results;
        }, {} as Record<string, React.ReactElement>);
        const slots = Object.entries(NAMES).reduce((results, [key, value], idx) => {
            results[value] = MappedChilds[value] || null;
            return results;
        }, {} as Record<string, React.ReactElement | null>);

        return slots;
    }, [children]);

    return (
        <>
            <StyledWrapper {...styles.wrapper} {...{ hideOnTablet }} {...otherProps}>
                <StyledWrapper {...styles.itemDescription}>
                    <Typography>{ItemDescription?.props?.children ?? translate("Item Description")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.priceUnit}>
                    {PriceUnit?.props?.children ?? translate("Price/Unit")}
                </StyledWrapper>
                <StyledWrapper {...styles.qty}>{Qty?.props?.children ?? translate("QTY")}</StyledWrapper>
                {showSurcharges && (
                    <StyledWrapper {...styles.surcharges}>
                        {Surcharges?.props?.children ?? translate("Surcharges")}
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.total}>{Total?.props?.children ?? translate("Total")}</StyledWrapper>
            </StyledWrapper>
            {hideOnTablet && <StyledWrapper {...styles.topSectionBorderStyles}></StyledWrapper>}
        </>
    );
};

LinesHeader.ItemDescription = ItemDescription;
LinesHeader.PriceUnit = PriceUnit;
LinesHeader.Qty = Qty;
LinesHeader.Total = Total;

LinesHeader.ItemDescription.displayName = NAMES.ItemDescription;
LinesHeader.PriceUnit.displayName = NAMES.PriceUnit;
LinesHeader.Qty.displayName = NAMES.Qty;
LinesHeader.Total.displayName = NAMES.Total;

export default LinesHeader;
